import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { INQUIRY } from '@/constants/testIds';
import { SPONSORSHIP_TIERS } from '@/lib/aawta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_CONFIG = {
  sponsor: {
    title: 'Sponsor Inquiry',
    description:
      'Tell us about your organization and we’ll follow up with sponsorship details tailored to your goals.',
    submitLabel: 'Submit Sponsor Inquiry',
    successMessage: 'Thank you! Our team will follow up within 1 business day.',
    fields: ['tier'],
  },
  ticket: {
    title: 'Reserve Your Seat',
    description:
      'Share your details and we’ll send you registration information as soon as it opens.',
    submitLabel: 'Request Registration Info',
    successMessage: 'You are on our list. Look out for registration details soon.',
    fields: [],
  },
  vendor: {
    title: 'Vendor Booth Inquiry',
    description:
      'Interested in exhibiting? Tell us a bit about your booth needs and we’ll get in touch.',
    submitLabel: 'Submit Vendor Inquiry',
    successMessage: 'Received! We will reach out with booth options shortly.',
    fields: ['booth'],
  },
  donation: {
    title: 'Support the Collaborative',
    description:
      'Every contribution directly funds workforce development, education, and economic mobility.',
    submitLabel: 'Send Donation Inquiry',
    successMessage: 'Thank you for supporting the movement. We will be in touch.',
    fields: ['amount'],
  },
  contact: {
    title: 'Send an Inquiry',
    description: 'We would love to hear from you. Send us a note and we will respond quickly.',
    submitLabel: 'Send Message',
    successMessage: 'Message sent. We will get back to you within 1 business day.',
    fields: [],
  },
  hotel: {
    title: 'Hotel Inquiry',
    description:
      'Tell us your travel dates and preferences — we will send partner hotel options and booking links directly.',
    submitLabel: 'Submit Hotel Inquiry',
    successMessage: 'Received! We will follow up with partner hotel options shortly.',
    fields: [],
  },
};

export const InquiryDialog = ({ open, onOpenChange, type = 'sponsor', prefill = {} }) => {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.contact;

  const [form, setForm] = useState({
    name: '',
    email: '',
    organization: '',
    phone: '',
    role: '',
    tier: '',
    booth_preference: '',
    amount: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        tier: prefill.tier || '',
      }));
    }
  }, [open, prefill.tier]);

  const setField = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: typeof e === 'string' ? e : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Name and email are required.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        type,
        name: form.name,
        email: form.email,
        organization: form.organization || null,
        phone: form.phone || null,
        role: form.role || null,
        tier: form.tier || null,
        booth_preference: form.booth_preference || null,
        amount: form.amount || null,
        message: form.message || null,
      };
      await axios.post(`${API}/inquiries`, payload);
      toast.success(config.successMessage);
      onOpenChange(false);
      setForm({
        name: '', email: '', organization: '', phone: '', role: '',
        tier: '', booth_preference: '', amount: '', message: '',
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Submission failed. Please try again in a moment.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={INQUIRY.dialog(type)}
        className="max-w-lg rounded-2xl border-aawta-border p-0 overflow-hidden"
      >
        <div className="bg-aawta-navy text-white p-6">
          <DialogHeader className="space-y-2">
            <div className="overline text-aawta-magenta">AAWTA Collaborative</div>
            <DialogTitle className="font-display text-2xl font-bold">
              {config.title}
            </DialogTitle>
            <DialogDescription className="text-white/75 text-[14.5px] leading-relaxed">
              {config.description}
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4 bg-white">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="inq-name">Full name *</Label>
              <Input
                id="inq-name"
                data-testid={INQUIRY.name}
                required
                value={form.name}
                onChange={setField('name')}
                placeholder="Jane Doe"
                className="rounded-lg"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inq-email">Email *</Label>
              <Input
                id="inq-email"
                type="email"
                data-testid={INQUIRY.email}
                required
                value={form.email}
                onChange={setField('email')}
                placeholder="jane@company.com"
                className="rounded-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="inq-org">Organization</Label>
              <Input
                id="inq-org"
                data-testid={INQUIRY.organization}
                value={form.organization}
                onChange={setField('organization')}
                placeholder="Your company or org"
                className="rounded-lg"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inq-phone">Phone</Label>
              <Input
                id="inq-phone"
                data-testid={INQUIRY.phone}
                value={form.phone}
                onChange={setField('phone')}
                placeholder="(555) 555-0100"
                className="rounded-lg"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inq-role">Your role</Label>
            <Input
              id="inq-role"
              data-testid={INQUIRY.role}
              value={form.role}
              onChange={setField('role')}
              placeholder="e.g. Director of Workforce Development"
              className="rounded-lg"
            />
          </div>

          {config.fields.includes('tier') && (
            <div className="space-y-1.5">
              <Label>Sponsorship tier of interest</Label>
              <Select value={form.tier} onValueChange={setField('tier')}>
                <SelectTrigger data-testid={INQUIRY.tier} className="rounded-lg">
                  <SelectValue placeholder="Select a tier" />
                </SelectTrigger>
                <SelectContent>
                  {SPONSORSHIP_TIERS.map((t) => (
                    <SelectItem key={t.slug} value={t.name}>
                      {t.name} · {t.price}
                    </SelectItem>
                  ))}
                  <SelectItem value="Custom / Not sure">Custom / Not sure</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {config.fields.includes('booth') && (
            <div className="space-y-1.5">
              <Label htmlFor="inq-booth">Booth preference</Label>
              <Input
                id="inq-booth"
                data-testid={INQUIRY.boothPreference}
                value={form.booth_preference}
                onChange={setField('booth_preference')}
                placeholder="e.g. 10x10 premium, corner, near entrance"
                className="rounded-lg"
              />
            </div>
          )}

          {config.fields.includes('amount') && (
            <div className="space-y-1.5">
              <Label htmlFor="inq-amount">Intended contribution</Label>
              <Input
                id="inq-amount"
                data-testid={INQUIRY.amount}
                value={form.amount}
                onChange={setField('amount')}
                placeholder="e.g. $500, $2,500, custom"
                className="rounded-lg"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="inq-message">Message</Label>
            <Textarea
              id="inq-message"
              data-testid={INQUIRY.message}
              value={form.message}
              onChange={setField('message')}
              placeholder="Tell us a bit about your goals or questions"
              rows={4}
              className="rounded-lg resize-none"
            />
          </div>

          <DialogFooter className="pt-2 gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid={INQUIRY.cancel}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              data-testid={INQUIRY.submit}
              className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white font-medium px-6 shadow-aawta-magenta"
            >
              {loading ? 'Submitting…' : config.submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InquiryDialog;
